import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { useCollectionsQuery } from "@/lib/queries";
import { useCurrency } from "@/context/CurrencyContext";
import { isSaleActive, effectivePriceGbp } from "@/lib/sale";
import { api } from "@/lib/api";

/**
 * Fair Lookbook — a standalone catalogue-style view of the current product line.
 *
 * Intentionally spartan:
 *   • No header / footer / cart drawer / WhatsApp bubble / currency selector.
 *   • Not linked from anywhere on the main site — reachable only by typing /lookbook.
 *   • Pulls live data from /api/collections (respects Global Sale visibility gate and
 *     Hidden stock_status via the same server-side filters as the main storefront).
 *
 * Two states: grid → expanded product carousel.
 */
export default function Lookbook() {
  const { data: items = [], isLoading } = useCollectionsQuery();
  const [openId, setOpenId] = useState(null);

  // /api/collections already excludes stock_status=Hidden, but defend anyway.
  const visible = useMemo(
    () => items.filter((p) => p.stock_status !== "Hidden"),
    [items],
  );

  const openProduct = visible.find((p) => p.id === openId) || null;

  useEffect(() => {
    // Full-screen lookbook — lock body scroll on the expanded overlay only.
    if (openProduct) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [openProduct]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="lookbook-page">
      {/* Section eyebrow — kept tiny and edge-hugging so photos own the page. */}
      <div className="px-4 sm:px-6 md:px-10 pt-8 pb-6 text-center">
        <div className="text-[0.7rem] tracking-[0.36em] uppercase text-[#D4AF37] font-semibold" data-testid="lookbook-eyebrow">
          Fair Lookbook
        </div>
        <div className="mt-2 text-white/50 text-xs sm:text-sm tracking-[0.18em] uppercase">
          Browse our latest editorial line
        </div>
      </div>

      {/* Full-screen grid */}
      {isLoading ? (
        <div className="min-h-[60vh] flex items-center justify-center text-white/40 text-sm tracking-[0.2em] uppercase" data-testid="lookbook-loading">
          Loading lookbook…
        </div>
      ) : visible.length === 0 ? (
        <div className="min-h-[60vh] flex items-center justify-center text-white/40 text-sm tracking-[0.2em] uppercase" data-testid="lookbook-empty">
          No pieces available at the moment.
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-1 md:gap-1.5"
          data-testid="lookbook-grid"
        >
          {visible.map((p, i) => (
            <LookbookCard
              key={p.id}
              product={p}
              index={i}
              onOpen={() => setOpenId(p.id)}
            />
          ))}
        </div>
      )}

      <div className="h-16" />

      <AnimatePresence>
        {openProduct && (
          <LookbookExpanded
            product={openProduct}
            onClose={() => setOpenId(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Grid card                                                                */
/* ----------------------------------------------------------------------- */
function LookbookCard({ product, index, onOpen }) {
  const { formatPrice } = useCurrency();
  const saleActive = isSaleActive(product);
  const displayPrice = formatPrice(effectivePriceGbp(product));

  return (
    <motion.button
      type="button"
      onClick={onOpen}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.03, 0.4) }}
      className="group relative block w-full overflow-hidden bg-[#0A0A0A] focus:outline-none"
      style={{ aspectRatio: "3/4" }}
      data-testid={`lookbook-card-${product.id}`}
      aria-label={`Open ${product.name}`}
    >
      {product.image ? (
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#151515] to-black" />
      )}

      {/* Gradient overlay — darker at the bottom so the label always reads. */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />

      {/* Sale ribbon — subtle, only when the item is truly on sale. */}
      {saleActive && (
        <div className="absolute top-3 left-3 text-[0.6rem] tracking-[0.28em] uppercase bg-[#D4AF37] text-black px-2.5 py-1 font-semibold">
          On Sale
        </div>
      )}

      {/* Bottom label — name in gold serif, price in white. */}
      <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 md:p-6">
        <div className="text-[0.6rem] sm:text-[0.65rem] tracking-[0.32em] uppercase text-[#D4AF37] font-semibold mb-1">
          {product.name}
        </div>
        <div className="flex items-baseline gap-2">
          {saleActive && (
            <div className="text-xs text-white/40 line-through">
              {formatPrice(product.price_gbp)}
            </div>
          )}
          <div className="font-serif text-lg sm:text-xl text-white">
            {displayPrice}
          </div>
        </div>
      </div>
    </motion.button>
  );
}

/* ----------------------------------------------------------------------- */
/* Expanded full-screen view with swipeable carousel                        */
/* ----------------------------------------------------------------------- */
function LookbookExpanded({ product, onClose }) {
  const { formatPrice } = useCurrency();
  const [detail, setDetail] = useState(product);
  const [idx, setIdx] = useState(0);

  // Lazy-load full detail (with gallery_images).
  useEffect(() => {
    setDetail(product);
    setIdx(0);
    if (!product?.id) return;
    let cancelled = false;
    api.get(`/collections/${product.id}`)
      .then((r) => { if (!cancelled) setDetail((p) => ({ ...p, ...r.data })); })
      .catch(() => { /* keep the light product if detail fetch fails */ });
    return () => { cancelled = true; };
  }, [product?.id]);

  const images = useMemo(
    () => [detail.image, ...(detail.gallery_images || [])].filter(Boolean),
    [detail],
  );
  const hasImages = images.length > 0;
  const multi = images.length > 1;
  const next = () => setIdx((i) => (i + 1) % images.length);
  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);

  // Keyboard: Esc to close, ← → to navigate.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && multi) prev();
      if (e.key === "ArrowRight" && multi) next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [multi, images.length, onClose]);

  const saleActive = isSaleActive(detail);
  const displayPrice = formatPrice(effectivePriceGbp(detail));

  // Compose description from the same fields the storefront uses.
  const descParts = [detail.texture, detail.length, (detail.hair_origin_custom || detail.hair_origin)]
    .map((s) => String(s || "").trim())
    .filter(Boolean);

  // --- swipe handlers ---
  const [touchStart, setTouchStart] = useState(null);
  const onTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStart == null) return;
    const delta = e.changedTouches[0].clientX - touchStart;
    if (delta > 50 && multi) prev();
    else if (delta < -50 && multi) next();
    setTouchStart(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[70] bg-black text-white flex flex-col"
      data-testid="lookbook-expanded"
    >
      {/* Top bar — back arrow + name/price. Kept sparse per brief. */}
      <div className="flex items-start justify-between gap-4 px-4 sm:px-6 md:px-10 pt-5 pb-4">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-2 text-white/70 hover:text-[#D4AF37] transition-colors"
          data-testid="lookbook-expanded-back"
          aria-label="Back to lookbook grid"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-[0.65rem] sm:text-xs uppercase tracking-[0.28em]">Back</span>
        </button>
        <div className="text-right flex-1 min-w-0">
          <div className="text-[0.6rem] sm:text-[0.65rem] tracking-[0.32em] uppercase text-[#D4AF37] font-semibold truncate" data-testid="lookbook-expanded-name">
            {detail.name}
          </div>
          <div className="flex items-baseline gap-2 justify-end mt-1">
            {saleActive && (
              <div className="text-xs text-white/40 line-through">
                {formatPrice(detail.price_gbp)}
              </div>
            )}
            <div className="font-serif text-base sm:text-lg text-white" data-testid="lookbook-expanded-price">
              {displayPrice}
            </div>
          </div>
        </div>
      </div>

      {/* Carousel — flex-1 so it fills available vertical space. */}
      <div
        className="relative flex-1 overflow-hidden bg-[#050505]"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {hasImages ? (
          <>
            <AnimatePresence mode="wait" initial={false}>
              <motion.img
                key={idx}
                src={images[idx]}
                alt={`${detail.name} — image ${idx + 1}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="absolute inset-0 w-full h-full object-contain"
                data-testid={`lookbook-expanded-image-${idx}`}
              />
            </AnimatePresence>

            {multi && (
              <>
                <button
                  type="button"
                  onClick={prev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full border border-[#D4AF37]/40 hover:border-[#D4AF37] bg-black/40 hover:bg-black/70 backdrop-blur-sm flex items-center justify-center text-[#D4AF37] transition-all"
                  aria-label="Previous image"
                  data-testid="lookbook-expanded-prev"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={next}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full border border-[#D4AF37]/40 hover:border-[#D4AF37] bg-black/40 hover:bg-black/70 backdrop-blur-sm flex items-center justify-center text-[#D4AF37] transition-all"
                  aria-label="Next image"
                  data-testid="lookbook-expanded-next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>

                {/* Dot indicators */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2" data-testid="lookbook-expanded-dots">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setIdx(i)}
                      className={`h-1.5 rounded-full transition-all ${
                        i === idx ? "w-6 bg-[#D4AF37]" : "w-1.5 bg-white/30 hover:bg-white/60"
                      }`}
                      aria-label={`Go to image ${i + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/40 text-sm tracking-[0.2em] uppercase">
            No imagery yet
          </div>
        )}
      </div>

      {/* Description — texture · length · origin, then the full copy. */}
      <div className="px-4 sm:px-6 md:px-10 py-6 border-t border-[#151515] bg-black">
        {descParts.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.65rem] sm:text-xs tracking-[0.24em] uppercase text-[#D4AF37]" data-testid="lookbook-expanded-attrs">
            {descParts.map((part, i) => (
              <span key={i} className="inline-flex items-center gap-3">
                {i > 0 && <span className="text-[#D4AF37]/40">·</span>}
                {part}
              </span>
            ))}
          </div>
        )}
        {(detail.long_description || detail.description) && (
          <p className="text-white/70 text-sm sm:text-[0.95rem] leading-relaxed mt-3 max-w-2xl" data-testid="lookbook-expanded-description">
            {detail.long_description || detail.description}
          </p>
        )}
      </div>
    </motion.div>
  );
}
